/**
 * em-switch-copy.js — the stockout-badge copy bank (DATA ONLY).
 * Source of truth for the words: docs/EMAMACO-SWITCH-COPY-BANK.md (repo).
 * em-family-configurator.js resolves a cell (stage + from->to identity match),
 * picks a rotating variant, fills tokens and renders the persuasion card on
 * ONE green chip. English only — the DE storefront (emLang='de') skips the
 * badge until the German translation pass.
 *
 * Cell matcher: stage must equal; every specified from/to attr must equal the
 * resolved identities ({length, feature}); optional dir ('longer'|'shorter')
 * compares SPECTRUM positions; optional sameLength requires from.length ===
 * to.length. Score = 2 per matched explicit attr + 1 per matched flag;
 * highest score wins, first-listed breaks ties. GEN-* cells are the safety
 * net so the badge never renders empty-handed.
 */
window.__emSwitchCopy = {
  version: 1,

  // Teaser-question vocabulary (collapsed card): who she is, in her words.
  geoNames: {
    AU: { demonym: 'Aussie', mums: 'mums' },
    NZ: { demonym: 'Kiwi', mums: 'mums' },
    UK: { demonym: 'British', mums: 'mums' },
    GB: { demonym: 'British', mums: 'mums' },
    IE: { demonym: 'Irish', mums: 'mums' },
    US: { demonym: 'American', mums: 'moms' },
    CA: { demonym: 'Canadian', mums: 'moms' },
    SG: { demonym: 'Singaporean', mums: 'mums' },
    DEFAULT: { demonym: '', mums: 'mums' }
  },
  stagePhrases: {
    maternity: 'in their 2nd + 3rd trimesters',
    postpartum: 'in their first weeks after birth',
    mum: ''
  },

  // Geo term swaps. Keyed by ISO country; DEFAULT covers the rest.
  terms: {
    AU: { pram: 'pram', card: 'Medicare card', schoolrun: 'school run', checkup: 'midwife appointment' },
    NZ: { pram: 'pram', card: 'bank card', schoolrun: 'school run', checkup: 'Plunket appointment' },
    UK: { pram: 'buggy', card: 'bank card', schoolrun: 'school run', checkup: 'midwife appointment' },
    GB: { pram: 'buggy', card: 'bank card', schoolrun: 'school run', checkup: 'midwife appointment' },
    IE: { pram: 'buggy', card: 'bank card', schoolrun: 'creche run', checkup: 'midwife appointment' },
    US: { pram: 'stroller', card: 'insurance card', schoolrun: 'school pickup', checkup: 'OB appointment' },
    CA: { pram: 'stroller', card: 'health card', schoolrun: 'school pickup', checkup: 'OB appointment' },
    SG: { pram: 'stroller', card: 'cards', schoolrun: 'school run', checkup: 'KKH check-up' },
    DEFAULT: { pram: 'pram', card: 'cards', schoolrun: 'school run', checkup: 'check-up' }
  },

  // The "oh wow" facts — true, scannable, referenced by ID from cells.
  facts: {
    P1: "Hospital advice from 36 weeks: phone on you, always — it's the contraction timer and the line you call your partner on when it starts",
    P2: "Pregnancy hands drop things — swollen wrists are real. And at 30+ weeks, picking something up off the floor is a whole event",
    P3: "Pregnancy hormones literally loosen your joints — that's why the handbag strap actually hurts now",
    P4: "Pelvic girdle pain hits about 1 in 5 pregnancies — carrying less is treatment",
    P5: "Nowhere to tuck your phone anymore — maternity leggings have no waistband, and maternity dresses never get pockets",
    P6: "Phone-in-the-bra stops working by mid-pregnancy — too sore, too warm",
    P7: "8-15 bathroom trips a day, most with nowhere to put anything",
    P8: "Daily walks are practically prescribed from mid-pregnancy",
    P9: "Late-pregnancy ankles swell by afternoon — a hem sitting on them leaves marks that ache",
    P10: "Your balance isn't what it was — you want one hand free on the stairs",
    P11: "Thigh chafe under summer dresses gets worse late pregnancy — above-knee coverage ends it",
    P12: "If there's already a toddler, one hand is permanently taken",
    R1: "Baby hugs + feeding pin you for 40+ minutes — the phone in reach is the difference between resting and staring at the wall",
    R2: "8-12 feeds a day, 2-4 overnight — the phone is the timer, the light and the sanity",
    R3: "Post-birth lifting advice: nothing heavier than the baby for six weeks. The heavy handbag is officially off the list",
    R4: "C-section mums: the high waist sits fully above the scar line — nothing rides across it",
    R5: "You're one-handed most of the day now — anything you need has to already be on you",
    R6: "Night sweats in the first weeks are hormones, not the weather — you want fabric that breathes",
    R7: "After the 6-week check-up it's daily {pram} walks — phone, keys and {card} come along, the bag doesn't",
    U1: "The toddler hand never gets returned — pockets are the hand you have left",
    U2: "Mums kept wearing their recovery pair so long we started selling it as shapewear. Same legging",
    U3: "School-gate maths: phone, keys, one snack, zero desire to carry a bag",
    L1: "7/8 ends above the afternoon ankle swell — nothing pressing where it puffs",
    L2: "7/8 looks full-length on shorter mums — it sits just above the ankle",
    L3: "Full length is the warm one — and looks dressed enough for the office and the school gate",
    L4: "3/4 is the coolest legging length — the hem clears the ankle AND the calf",
    L5: "Shorts carry the same waist support with the least fabric — under-dress or gym, they disappear",
    L5R: "Same firm waist, least fabric — under a dress for visitors or just surviving a warm day, nobody knows they're there",
    L6: "Long shorts: above-knee coverage that ends thigh chafe under dresses",
    K1: "The bump changes your whole shape — except the side of your thigh. That's exactly where these pockets sit",
    K1R: "Side-thigh pockets that hug the leg, made like this so the tummy can heal",
    K2: "Deep enough that the phone lies flat along the thigh — no outline when standing",
    K3: "No-pockets is the smoothest possible line under a fitted dress — nothing showing through",
    K4: "Phone, keys, {card} on you — carried by the leggings, not your shoulder"
  },

  // Close swap when the badge target is a PRE-ORDER chip.
  preorderClose: {
    base: "Fresh batch lands {ship}. Orders are filled first come, first served — reserve {size} now.",
    due: "It lands {ship} — you'll be {week} weeks. Reserve {size}."
  },
  preorderKicker: "Worth the short wait",

  // Cells. s = scene (string or {south,north,equator}); f = fact IDs; c = close.
  cells: [
    // ── RECOVERY (stage 'postpartum') ─────────────────────────────────
    { id: 'RC-1', stage: 'postpartum', from: { length: 'shorts' }, to: { length: '7/8', feature: 'with pockets' }, variants: [
      { k: 'Why new mums switch',
        s: "You're up at 3am feeding either way — these are the leggings built for exactly these weeks.",
        f: ['R2', 'R4', 'K4'],
        c: "7 in 10 recovery mums end up in this exact pair — take {size} while it's still in stock." },
      { k: 'What new mums pick',
        s: "The 6-week check-up ends with one instruction: walk. {pram} out the door, phone in the thigh pocket, nothing on your shoulder.",
        f: ['R7', 'R3', 'L2'],
        c: "When the shorts sell out, this is the pair mums pick — two to one. {size} is in stock today." },
      { k: 'Why new mums switch',
        s: { south: "Shorts weather is coming; recovery is now. Mums prefer the 7/8 length for both winter + spring.",
             north: "Summer outside, hormones inside. Mums prefer the 7/8 length for both summer + autumn.",
             equator: "Recovery in the humidity: mums prefer the 7/8 length all year round here." },
        f: ['R6', 'R1', 'K1R'],
        c: "Our most re-ordered recovery item, full stop. {size} is here now." }
    ] },
    { id: 'RC-2', stage: 'postpartum', from: { length: 'full length', feature: 'with pockets' }, to: { length: '7/8', feature: 'with pockets' }, variants: [
      { k: 'What new mums pick',
        s: "Everything you wanted — the firm tummy-healing waist, the deep pockets — with a hem that stops above swollen ankles instead of sitting on them.",
        f: ['L1', 'L2', 'R4'],
        c: "4 of every 5 {size}s we sell in this range are the 7/8. There's a reason." },
      { k: 'Why new mums switch',
        s: "This is the one mums refuse to retire — the exact legging we ended up selling as everyday shapewear because nobody would stop wearing it.",
        f: ['U2', 'K2', 'R5'],
        c: "The most re-bought pair we make. {size} in stock — that changes fast." }
    ] },
    { id: 'RC-3', stage: 'postpartum', from: { length: 'full length', feature: 'with pockets' }, to: { length: 'full length', feature: 'no pockets' }, variants: [
      { k: 'What new mums pick',
        s: "Same compression, same warmth — this is the one under a dress at a christening or a naming day, when you want nothing showing through.",
        f: ['K3', 'R4', 'L3'],
        c: "The no-pockets is the quiet favourite for exactly this. {size} is here." },
      { k: 'Why new mums switch',
        s: "First weeks truth: the phone lives in the {pram} caddy and the couch cushion, not your pocket. Take the smoother look while your size is here.",
        f: ['R1', 'K3', 'L3'],
        c: "Mums in the first 6 weeks pick the smooth version 2-to-1. {size}, today." }
    ] },
    { id: 'RC-4', stage: 'postpartum', from: { length: '3/4' }, to: { length: '7/8', feature: 'with pockets' }, variants: [
      { k: 'What new mums pick',
        s: "Same waist, same pockets, one hem-length difference you'll stop noticing by the second wear — and it still clears the ankle swell.",
        f: ['L1', 'L2', 'K2'],
        c: "The 7/8 outsells the 3/4 two-to-one in recovery. {size} is in." },
      { k: 'Why new mums switch',
        s: { south: "By the time bub's napping properly you'll be out walking spring mornings — the 7/8 covers your legs for them; the 3/4 was built for the heat that's already gone.",
             north: "The 3/4 is a summer hem. You're recovering into autumn — the 7/8 carries you there without buying twice.",
             equator: "Same heat every month here — but aircon everywhere too. The 7/8 is the one that works in both." },
        f: ['R7', 'L1', 'K4'],
        c: "This is the length recovery mums end up living in. {size} today." }
    ] },
    { id: 'RC-5', stage: 'postpartum', from: { length: '7/8', feature: 'no pockets' }, to: { length: '7/8', feature: 'with pockets' }, variants: [
      { k: 'Why new mums switch',
        s: "Your hands are full now — permanently. The pocket version gives you one back.",
        f: ['R5', 'R3', 'K2'],
        c: "Pockets outsell no-pockets two-to-one in recovery. The pocket version has {size} right now." },
      { k: 'What new mums pick',
        s: "If you skipped pockets to keep the line clean: these sit flat on the side-thigh and vanish when you stand. You keep the clean look — and your phone finally has somewhere to live.",
        f: ['K2', 'K1R', 'R1'],
        c: "Most mums who swore by no-pockets never go back after this pair. {size}, while it's here." },
      { k: 'Why new mums switch',
        s: "Feed timer, white noise, torch: the phone is the whole night shift, and the pocket means it's never on the far side of the room.",
        f: ['R2', 'R1', 'K4'],
        c: "7 in 10 recovery mums take the pockets. Take {size}." }
    ] },
    { id: 'RC-6', stage: 'postpartum', from: {}, to: { length: 'shorts', feature: 'with pockets' }, dir: 'shorter', variants: [
      { k: 'What new mums pick',
        s: "The same recovery waist with the least fabric — under a dress for visitors, under trackies when it's cold, on their own when it's not.",
        f: ['L5R', 'R4', 'R6'],
        c: "The quiet bestseller of the range — mums come back for a second pair within the month. {size} is in." },
      { k: 'Why new mums switch',
        s: { south: "Night sweats don't wait for summer — shorts under a big jumper is the real fourth-trimester uniform.",
             north: "Peak-summer recovery: shorts carry the compression without the cling. Aircon nights, big-shirt days.",
             equator: "Thirty-two degrees, every day, forever: shorts are the only honest answer with this much support in them." },
        f: ['R6', 'L5', 'K4'],
        c: "When it's warm these outsell every legging we make. {size}, today." }
    ] },
    { id: 'RC-7', stage: 'postpartum', from: { length: 'shorts' }, to: { length: 'full length', feature: 'no pockets' }, variants: [
      { k: 'What new mums pick',
        s: "Same firm tummy-healing waist you came for — carried all the way down for the couch feeds, the cold kitchen at 3am, the {pram} laps.",
        f: ['R2', 'R4', 'L3'],
        c: "In the first weeks the full length is the 2-to-1 pick. {size} is in." },
      { k: 'Why new mums switch',
        s: "Visitors week: one smooth line under a big shirt, nothing showing through, compression quietly doing its job underneath.",
        f: ['K3', 'R6', 'R4'],
        c: "The quiet bestseller of early recovery. Take {size} while it's here." }
    ] },

    // ── MATERNITY ─────────────────────────────────────────────────────
    { id: 'MT-1', stage: 'maternity', from: { length: '7/8', feature: 'no pockets' }, to: { length: '7/8', feature: 'with pockets' }, variants: [
      { k: 'Why pregnant mums switch',
        s: "The last stretch has one rule: phone on you, always. The thigh pocket is where it lives — flat, invisible, there when it matters.",
        f: ['P1', 'P5', 'K2'],
        c: "Mums past 30 weeks take the pockets 2-to-1. Take {size} while it's here." },
      { k: 'What pregnant mums pick',
        s: "Hated pocket leggings before? Fair — they used to bulge. But the bump has changed your shape anyway, and the side of your thigh is the one spot that still looks smooth. That's where these pockets sit.",
        f: ['K2', 'P6', 'P5'],
        c: "7 in 10 second- and third-trimester mums order the pockets. {size} is in stock." },
      { k: 'Why pregnant mums switch',
        s: "One hand for the toddler, one for the rail — and the handbag now hurts places you didn't know could hurt. Keys, phone, {card}: carried by the leggings instead of your shoulder.",
        f: ['P3', 'P4', 'P12'],
        c: "The pockets version is what mums at your stage actually order. {size}, today." }
    ] },
    { id: 'MT-2', stage: 'maternity', from: { length: 'full length', feature: 'no pockets' }, to: { length: 'full length', feature: 'with pockets' }, variants: [
      { k: 'What pregnant mums pick',
        s: "Everything you picked full-length FOR — warmth, coverage, looks dressed — with the one upgrade pregnancy actually demands: somewhere for the phone to live.",
        f: ['P1', 'P7', 'K2'],
        c: "The pockets version outsells plain 2-to-1 after 20 weeks. {size} is in." },
      { k: 'Why pregnant mums switch',
        s: "Pregnancy hands drop things — and at 30-plus weeks every pick-up is a squat. The pocket ends most of the squats.",
        f: ['P2', 'P10', 'K4'],
        c: "7 in 10 mums this trimester take the pockets. Take {size}." }
    ] },
    { id: 'MT-3', stage: 'maternity', from: { length: 'shorts', feature: 'no pockets' }, to: { length: 'shorts', feature: 'with pockets' }, variants: [
      { k: 'Why pregnant mums switch',
        s: "Daily walks are basically prescribed from here — shorts on, phone and keys in the thigh pocket, both hands free for the whole lap.",
        f: ['P8', 'P1', 'K2'],
        c: "The pocket shorts are the warm-months bestseller. {size} today." },
      { k: 'What pregnant mums pick',
        s: "Under a dress at a wedding, third trimester: support where you need it, a pocket where the {card} and phone hide, no bag to babysit all night.",
        f: ['L5', 'P7', 'K1'],
        c: "Mums buy these for one event and wear them for a trimester. {size} is in stock." }
    ] },
    { id: 'MT-4', stage: 'maternity', from: { length: '3/4' }, to: { length: '7/8', feature: 'with pockets' }, variants: [
      { k: 'What pregnant mums pick',
        s: { south: "Quick maths: you'll be at your biggest as the weather warms — the 7/8 breathes at the ankle now AND covers the cool mornings between here and there.",
             north: "Quick maths: you'll be at your biggest heading into the cold — the 3/4 is a summer hem; the 7/8 is the one that goes the distance.",
             equator: "Same heat every month here — the 7/8 stays off the ankle swell and works in every aircon setting Singapore owns." },
        f: ['P9', 'L1', 'L2'],
        c: "The 7/8 is the length pregnant mums end up living in — 4 of every 5 {size}s go this way." },
      { k: 'Why pregnant mums switch',
        s: "Third-trimester ankles swell by afternoon. The 7/8 ends above the puff — nothing pressing, no sock-line ache, still looks full-length.",
        f: ['P9', 'L2', 'K2'],
        c: "Our top maternity seller by a distance. {size} is in stock now." }
    ] },
    { id: 'MT-5', stage: 'maternity', from: { length: 'long shorts' }, to: { length: 'shorts', feature: 'with pockets' }, variants: [
      { k: 'What pregnant mums pick',
        s: "You picked long shorts for coverage — the bike short covers the chafe zone too, with the same waist support, and it has {size} in stock today.",
        f: ['P11', 'L5', 'K4'],
        c: "The bike short is the one mums come back and buy a second pair of. Take {size}." }
    ] },
    { id: 'MT-6', stage: 'maternity', from: { length: 'full length' }, to: { length: '7/8', feature: 'with pockets' }, variants: [
      { k: 'Why pregnant mums switch',
        s: { south: "Due when it turns warm? You'll be at your biggest in the warm months — the 7/8 stops above the ankle swell, and your phone lives in the thigh pocket on every walk between now and then.",
             north: "Summer now, biggest later: the 7/8 breathes today and layers under everything when it turns — phone in the thigh pocket the whole way.",
             equator: "In this humidity the full length never gets its turn — the 7/8 is what actually gets worn, KKH waiting rooms included." },
        f: ['P9', 'P1', 'K2'],
        c: "This is the version third-trimester mums are taking right now. {size} is in." }
    ] },
    { id: 'MT-7', stage: 'maternity', from: { feature: 'with pockets' }, to: { feature: 'no pockets' }, sameLength: true, variants: [
      { k: 'What pregnant mums pick',
        s: "For the weeks of {checkup}s, baby showers and bump photos: the plain version is the one that disappears under a fitted dress.",
        f: ['K3', 'L3', 'P5'],
        c: "The no-pockets is the events pick — mums keep one of each. {size} is in stock." },
      { k: 'Why pregnant mums switch',
        s: "Heading somewhere with a bag anyway? Take the smoother look today — the support is identical, and {size} is on the shelf now.",
        f: ['K3', 'P9', 'L2'],
        c: "One in three mums keeps both versions. Start with the one in stock." }
    ] },

    // ── MUM ───────────────────────────────────────────────────────────
    { id: 'MU-1', stage: 'mum', from: { length: 'full length' }, to: { length: '7/8' }, variants: [
      { k: 'What mums pick',
        s: "This is the recovery legging mums refused to stop wearing — we renamed it because the reorders wouldn't quit. The 7/8 is the cut they come back for.",
        f: ['U2', 'L2', 'L1'],
        c: "Most-rebought item in the range. {size} is in stock today." }
    ] },
    { id: 'MU-2', stage: 'mum', from: { feature: 'no pockets' }, to: { feature: 'with pockets' }, variants: [
      { k: 'Why mums switch',
        s: "Phone, keys, one emergency snack, zero desire to carry a bag — the {schoolrun} has a uniform and this is it.",
        f: ['U3', 'U1', 'K2'],
        c: "Pockets outsell plain 2-to-1 with mums. Take {size}." }
    ] },
    { id: 'MU-3', stage: 'mum', from: { length: '7/8' }, to: { length: 'full length' }, variants: [
      { k: 'What mums pick',
        s: { south: "School-gate mornings are still single digits — the full length is the warm one, and it reads dressed enough for everything after.",
             north: "The full length is the autumn-into-winter pick — warm on the {schoolrun}, polished enough for the office.",
             equator: "The full length is the aircon-office pick — cold floors at work, one legging that reads dressed." },
        f: ['L3', 'U1', 'K2'],
        c: "The cold-months favourite, every year. {size} is in." }
    ] },

    // ── GENERIC FALLBACKS (safety net — least specific, listed last) ──
    { id: 'GEN-RC-pocket-add', stage: 'postpartum', from: { feature: 'no pockets' }, to: { feature: 'with pockets' }, variants: [
      { k: 'Why new mums switch',
        s: "One-handed is the default setting now — the pocket version carries the phone so your last free hand doesn't have to.",
        f: ['R5', 'R1', 'K2'],
        c: "Pockets outsell plain 2-to-1 in recovery. Take {size}." }
    ] },
    { id: 'GEN-RC-pocket-remove', stage: 'postpartum', from: { feature: 'with pockets' }, to: { feature: 'no pockets' }, variants: [
      { k: 'What new mums pick',
        s: "Same compression, cleaner line — the version that disappears under anything while your size is actually here.",
        f: ['K3', 'R4', 'R6'],
        c: "The smooth version is the sleeper favourite. {size}, today." }
    ] },
    { id: 'GEN-RC-longer', stage: 'postpartum', from: {}, to: {}, dir: 'longer', variants: [
      { k: 'What new mums pick',
        s: "More coverage for the season you're actually recovering in — same waist, same squeeze, warmer leg.",
        f: ['R6', 'R7', 'L3'],
        c: "The longer cut is what recovery mums end up living in. {size} is in." }
    ] },
    { id: 'GEN-RC-shorter', stage: 'postpartum', from: {}, to: {}, dir: 'shorter', variants: [
      { k: 'Why new mums switch',
        s: "Same recovery waist, less fabric — postpartum runs hot, and the shorter cut is the honest answer.",
        f: ['R6', 'L1', 'L5'],
        c: "The shorter cut sells out first most months. {size} is here now." }
    ] },
    { id: 'GEN-MT-pocket-add', stage: 'maternity', from: { feature: 'no pockets' }, to: { feature: 'with pockets' }, variants: [
      { k: 'Why pregnant mums switch',
        s: "From here the phone has to live ON you — and pregnancy took away everywhere else it used to go.",
        f: ['P1', 'P5', 'K2'],
        c: "Mums past 20 weeks take the pockets 2-to-1. Take {size}." }
    ] },
    { id: 'GEN-MT-pocket-remove', stage: 'maternity', from: { feature: 'with pockets' }, to: { feature: 'no pockets' }, variants: [
      { k: 'What pregnant mums pick',
        s: "The plain version is the one under fitted dresses at every event of this trimester — identical support, invisible line.",
        f: ['K3', 'P5', 'L3'],
        c: "Mums keep one of each — start with the one holding {size}." }
    ] },
    { id: 'GEN-MT-longer', stage: 'maternity', from: {}, to: {}, dir: 'longer', variants: [
      { k: 'What pregnant mums pick',
        s: "You'll be at your biggest a season from now — the longer cut is the one that goes the distance without buying twice.",
        f: ['P8', 'P10', 'L3'],
        c: "The longer length is the trimester-three favourite. {size} is in." }
    ] },
    { id: 'GEN-MT-shorter', stage: 'maternity', from: {}, to: {}, dir: 'shorter', variants: [
      { k: 'Why pregnant mums switch',
        s: "Bumps run hot and ankles swell by afternoon — the shorter cut breathes and stops above the puff.",
        f: ['P9', 'P8', 'L1'],
        c: "The shorter cut is what mums your week are taking. {size}, today." }
    ] },
    { id: 'GEN-MU-pocket-add', stage: 'mum', from: { feature: 'no pockets' }, to: { feature: 'with pockets' }, variants: [
      { k: 'Why mums switch',
        s: "Phone, keys, one emergency snack, no desire to carry a bag — the pocket version is the {schoolrun} uniform.",
        f: ['U3', 'U1', 'K2'],
        c: "Pockets outsell plain 2-to-1 with mums. Take {size}." }
    ] },
    { id: 'GEN-MU-pocket-remove', stage: 'mum', from: { feature: 'with pockets' }, to: { feature: 'no pockets' }, variants: [
      { k: 'What mums pick',
        s: "The smoothest line in the range — the one that works under a dress as hard as it works at the gym.",
        f: ['K3', 'U2', 'L3'],
        c: "The clean version is the repeat-buy. {size} is in stock." }
    ] },
    { id: 'GEN-MU-longer', stage: 'mum', from: {}, to: {}, dir: 'longer', variants: [
      { k: 'What mums pick',
        s: "The longer cut carries the cold months and still reads dressed — one legging, every setting.",
        f: ['L3', 'U2', 'K2'],
        c: "The winter pick, year after year. Take {size}." }
    ] },
    { id: 'GEN-MU-shorter', stage: 'mum', from: {}, to: {}, dir: 'shorter', variants: [
      { k: 'Why mums switch',
        s: "Warm-months truth: the shorter cut gets worn double the days — same support, half the heat.",
        f: ['L1', 'L4', 'U2'],
        c: "The warm-weather pick, year after year. {size} is here." }
    ] }
  ]
};
